/**
 * Reads pages created in the editing system and hands out domain pages.
 *
 * This file is the only place in the application that knows how the CMS stores
 * a page. Everything the storage format makes awkward is dealt with here, so
 * that PageUseCase and the components above it never see it:
 *
 *  - **Both languages live in one file**, keyed by locale. A page therefore
 *    cannot exist in German and be missing in English, which is what keeps the
 *    hreflang set honest.
 *  - **Structure is duplicated into every language, text is not.** German is
 *    the structure of record: the CMS writes the block type only into the
 *    leading language, so a translated section arrives as text with no type and
 *    would not be a section at all. `toBlocks` lays the translation over the
 *    German original before anything is validated.
 *  - **Blank means untranslated, not empty.** Same rule at every level.
 *
 * If the storage format ever changes - a different CMS, a split into separate
 * files per language - this is the file that changes. Nothing else.
 *
 * Also loaded by scripts/prerender.js at build time, so no package imports.
 */

import { CMS_PAGE_FILES } from '../../../content/pages.generated';
import { Page, PageContent } from '../../../corelogic/model/page';
import { DEFAULT_LANGUAGE, LANGUAGES, Language } from '../../../shared/constants/routes';
import { toBlocks } from './blockMapper';
import { toCmsShape } from './pageFileFormat';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const str = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed === '' ? undefined : trimmed;
};

/**
 * Content problems are for whoever can fix them - the build log and the dev
 * server - not for a visitor's console on the live site. Same guard as
 * blockMapper.ts; the prerenderer runs without NODE_ENV set and so still sees
 * every warning.
 */
const warn = (message: string) => {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(`[pages] ${message}`);
  }
};

/**
 * The CMS stores both languages in one file, keyed by locale. If that ever
 * changes shape, a flat file is read as the default language rather than
 * dropping the page.
 */
const localeSlice = (data: Record<string, unknown>, language: Language): Record<string, unknown> => {
  const slice = data[language];
  if (isRecord(slice)) {
    return slice;
  }
  return language === DEFAULT_LANGUAGE ? data : {};
};

/** First paragraph of body text, used when no meta description was written. */
const firstParagraph = (sections: PageContent['sections']): string | undefined => {
  for (const section of sections) {
    if (section.type === 'text' && section.paragraphs.length > 0) {
      return section.paragraphs[0];
    }
  }
  return undefined;
};

const truncate = (text: string, limit = 155): string =>
  text.length <= limit ? text : `${text.slice(0, text.lastIndexOf(' ', limit - 1))}…`;

const toContent = (
  raw: Record<string, unknown>,
  fallback: PageContent | undefined,
  defaultRaw: Record<string, unknown> | undefined,
  slug: string
): PageContent | undefined => {
  const title = str(raw.title) ?? fallback?.title;
  if (!title) {
    return undefined;
  }

  const lead = str(raw.lead) ?? fallback?.lead;
  // The leading language fills the gaps a translator left, which is also what
  // turns translated words back into sections - see toBlocks.
  const sections = toBlocks(raw.blocks, defaultRaw?.blocks);

  const description =
    str(raw.seoDescription) ?? lead ?? firstParagraph(sections) ?? fallback?.seo.description ?? '';

  if (!description) {
    warn(`page "${slug}" has no description - search results and link previews will be blank`);
  }

  return {
    title,
    titleImage: str(raw.titleImage) ?? fallback?.titleImage,
    eyebrow: str(raw.eyebrow) ?? fallback?.eyebrow,
    lead,
    icon: str(raw.icon) ?? fallback?.icon,
    navLabel: str(raw.navLabel) ?? title,
    seo: {
      title: str(raw.seoTitle) ?? `${title} | M.E.-Kollektiv`,
      description: truncate(description),
      keywords: str(raw.seoKeywords) ?? fallback?.seo.keywords,
    },
    sections,
  };
};

/** Exported for the tests, which exercise the translation seam directly. */
export const toPage = (slug: string, raw: unknown): Page | undefined => {
  if (!isRecord(raw)) {
    warn(`page "${slug}" is not an object - skipped`);
    return undefined;
  }

  // The file separates structure from words; everything below works on the
  // flat per-language shape, the same one the CMS edits in.
  const data = toCmsShape(raw);
  const defaultSlice = localeSlice(data, DEFAULT_LANGUAGE);

  if (defaultSlice.published === false) {
    return undefined;
  }

  const defaultContent = toContent(defaultSlice, undefined, undefined, slug);
  if (!defaultContent) {
    warn(`page "${slug}" has no title in "${DEFAULT_LANGUAGE}" - skipped`);
    return undefined;
  }

  const content = {} as Record<Language, PageContent>;
  LANGUAGES.forEach((language) => {
    if (language === DEFAULT_LANGUAGE) {
      content[language] = defaultContent;
      return;
    }

    const slice = localeSlice(data, language);
    const translated = toContent(slice, defaultContent, defaultSlice, slug);
    // toContent only returns undefined without a title, and the fallback has
    // one, so this is unreachable - it just keeps the type honest.
    content[language] = translated ?? defaultContent;

    if (!str(slice.title)) {
      warn(`page "${slug}" is not translated to "${language}" - showing the ${DEFAULT_LANGUAGE} text`);
    }
  });

  return { slug, path: `/${slug}`, showInNav: defaultSlice.showInNav === true, content };
};

/** Every published page, in file-name order. */
export const CMS_PAGES: Page[] = CMS_PAGE_FILES.map((file) => toPage(file.slug, file.data)).filter(
  (page): page is Page => page !== undefined
);
